import {openDeleteDialog, showNotification} from 'platform/components';
import {match} from 'ts-pattern';

import {always, isNil} from 'ramda';

import {
  RentalOrderResponseBody,
  useCancelRentalOrderVehicleMutation,
  useRemoveRentalOrderMutation,
} from '@dms/api/rental';
import i18n from '@dms/i18n';
import {rentalOrderRoutes, testIds} from '@dms/routes';

import {Nullish, useNavigate} from 'shared/web';

import {EntityHeaderAction} from '../components/EntityHeaderV2/types';
import {handleApiError} from '../utils/handleApiError';

export const useGetRentalOrderActions = (
  rentalOrder: RentalOrderResponseBody | Nullish
): EntityHeaderAction[] => {
  const navigate = useNavigate();

  const [removeRentalOrder, {isLoading: isRemovingRentalOrder}] = useRemoveRentalOrderMutation();
  const [cancelRentalOrder, {isLoading: isCancellingRentalOrder}] =
    useCancelRentalOrderVehicleMutation();

  if (isNil(rentalOrder)) {
    return [
      {
        type: 'button',
        variant: 'secondary',
        title: i18n.t('general.actions.discard'),
        onClick: () => navigate(rentalOrderRoutes.overview),
        'data-testid': testIds.rentalOrder.detail('discard'),
      },
    ];
  }

  const handleCancelOrder = () => {
    openDeleteDialog({
      onConfirm: () =>
        cancelRentalOrder({rentalOrderId: rentalOrder.rentalOrderId})
          .unwrap()
          .then(() =>
            showNotification.success(i18n.t('general.notifications.successfullyCancelled'))
          )
          .catch(handleApiError),
      text: i18n.t('page.rentalOrder.labels.cancelOrderDescription'),
      'data-testid': testIds.rentalOrder.detail('delete-order-dialog'),
    });
  };

  const handleRemoveOrder = () =>
    openDeleteDialog({
      onConfirm: () =>
        removeRentalOrder({rentalOrderId: rentalOrder.rentalOrderId})
          .unwrap()
          .then(() => showNotification.success(i18n.t('general.notifications.successfullyDeleted')))
          .then(() => navigate(rentalOrderRoutes.overview))
          .catch(handleApiError),
      text: i18n.t('page.rentalOrder.labels.deleteOrderDescription'),
      'data-testid': testIds.rentalOrder.detail('delete-order-dialog'),
    });

  return match<RentalOrderResponseBody['state'], EntityHeaderAction[]>(rentalOrder.state)
    .with(
      'draft',
      'canceled',
      always([
        {
          type: 'button',
          variant: 'secondary',
          title: i18n.t('general.actions.delete'),
          onClick: handleRemoveOrder,
          isLoading: isRemovingRentalOrder,
          'data-testid': testIds.rentalOrder.detail('delete'),
        },
      ])
    )
    .with(
      'reserved',
      'returned',
      'closed',
      always([
        {
          type: 'button',
          variant: 'secondary',
          title: i18n.t('page.rentalOrder.labels.cancelOrder'),
          onClick: handleCancelOrder,
          isLoading: isCancellingRentalOrder,
          'data-testid': testIds.rentalOrder.detail('cancel'),
        },
      ])
    )
    .otherwise(always([]));
};
